import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash2, Edit2, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { slugify } from '@/lib/utils';

const emptyPost = {
    slug: '', title: '', excerpt: '', content: '', featured_image: '',
    categories: [], tags: [], author: 'Škobić Keramika', faq: [],
    related_posts: [], related_services: [], cta_text: '',
    seo: { meta_title: '', meta_description: '', canonical_url: '', robots_index: true, robots_follow: true, og_title: '', og_description: '', og_image: '', include_in_sitemap: true, sitemap_priority: 0.5, sitemap_changefreq: 'monthly' },
    status: 'published'
};

export default function AdminBlog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = () => { setLoading(true); api.get('/admin/blog').then(({ data }) => setPosts(data)).finally(() => setLoading(false)); };
    useEffect(load, []);

    const open = (item) => {
        setEditing(item ? {
            ...item,
            categories: item.categories || [], tags: item.tags || [],
            faq: item.faq || [], related_posts: item.related_posts || [], related_services: item.related_services || [],
            seo: item.seo || emptyPost.seo
        } : { ...emptyPost });
        setDialogOpen(true);
    };
    const close = () => { setDialogOpen(false); setEditing(null); };

    const onSave = async () => {
        setSaving(true);
        try {
            const payload = { ...editing };
            if (!payload.slug) payload.slug = slugify(payload.title);
            if (editing.id) { await api.put(`/admin/blog/${editing.id}`, payload); toast.success('Objava ažurirana'); }
            else { await api.post('/admin/blog', payload); toast.success('Objava kreirana'); }
            close(); load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Spremanje nije uspjelo'); } finally { setSaving(false); }
    };

    const onDelete = async (id) => {
        if (!confirm('Obrisati objavu?')) return;
        try { await api.delete(`/admin/blog/${id}`); toast.success('Obrisano'); load(); } catch { toast.error('Brisanje nije uspjelo'); }
    };

    const addFAQ = () => setEditing({...editing, faq: [...(editing.faq||[]), {question: '', answer: ''}]});
    const updateFAQ = (i, k, v) => { const f = [...editing.faq]; f[i] = {...f[i], [k]: v}; setEditing({...editing, faq: f}); };
    const removeFAQ = (i) => setEditing({...editing, faq: editing.faq.filter((_,j) => j !== i)});

    const setSeo = (k, v) => setEditing({...editing, seo: {...editing.seo, [k]: v}});

    return (
        <div>
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-medium">Blog</h1>
                    <p className="text-sm text-white/60 mt-1">Blog članci s SEO postavkama, FAQ i CTA.</p>
                </div>
                <Button onClick={() => open(null)} className="bg-[#B08D57] hover:bg-[#9c7d4e] text-white"><Plus className="mr-2 h-4 w-4" />Nova objava</Button>
            </div>

            <Card className="mt-6 bg-[#111318] border-[#232A36] overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="text-white/60 text-xs uppercase tracking-wider">
                        <tr className="border-b border-[#232A36]">
                            <th className="text-left px-4 py-3">Naslov</th>
                            <th className="text-left px-4 py-3">Slug</th>
                            <th className="text-left px-4 py-3">Status</th>
                            <th className="text-right px-4 py-3">Akcije</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? <tr><td colSpan="4" className="p-8 text-center text-white/60">Učitavanje…</td></tr> :
                          posts.map((p) => (
                            <tr key={p.id} className="border-b border-[#232A36] hover:bg-[#161A21]">
                                <td className="px-4 py-3 font-medium">{p.title}</td>
                                <td className="px-4 py-3 text-white/70 font-mono text-xs">{p.slug}</td>
                                <td className="px-4 py-3 text-white/70">{p.status}</td>
                                <td className="px-4 py-3 text-right">
                                    <Button size="sm" variant="outline" onClick={() => open(p)} className="border-[#232A36] text-white hover:bg-[#232A36] mr-2"><Edit2 className="h-3.5 w-3.5" /></Button>
                                    <Button size="sm" variant="outline" onClick={() => onDelete(p.id)} className="border-red-900 text-red-400 hover:bg-red-900/20"><Trash2 className="h-3.5 w-3.5" /></Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-4xl bg-[#111318] border-[#232A36] text-white max-h-[92vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editing?.id ? 'Uredi objavu' : 'Nova objava'}</DialogTitle></DialogHeader>
                    {editing && (
                        <Tabs defaultValue="content">
                            <TabsList className="bg-[#161A21]">
                                <TabsTrigger value="content">Sadržaj</TabsTrigger>
                                <TabsTrigger value="faq">FAQ</TabsTrigger>
                                <TabsTrigger value="seo">SEO</TabsTrigger>
                                <TabsTrigger value="linking">Linkovi</TabsTrigger>
                            </TabsList>
                            <TabsContent value="content" className="space-y-4 mt-4">
                                <div><Label>Naslov</Label><Input value={editing.title} onChange={(e) => setEditing({...editing, title: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                                <div><Label>Slug</Label><Input value={editing.slug} onChange={(e) => setEditing({...editing, slug: slugify(e.target.value)})} className="mt-2 bg-[#0B0C0E] border-[#232A36] font-mono" /></div>
                                <div><Label>Featured slika (URL)</Label><Input value={editing.featured_image} onChange={(e) => setEditing({...editing, featured_image: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                                <div><Label>Sažetak (excerpt)</Label><Textarea value={editing.excerpt} onChange={(e) => setEditing({...editing, excerpt: e.target.value})} rows={2} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                                <div><Label>Sadržaj (HTML)</Label><Textarea value={editing.content} onChange={(e) => setEditing({...editing, content: e.target.value})} rows={15} className="mt-2 bg-[#0B0C0E] border-[#232A36] font-mono text-xs" /></div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><Label>Kategorije (zarezom)</Label><Input value={(editing.categories||[]).join(', ')} onChange={(e) => setEditing({...editing, categories: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                                    <div><Label>Tagovi (zarezom)</Label><Input value={(editing.tags||[]).join(', ')} onChange={(e) => setEditing({...editing, tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                                </div>
                                <div><Label>CTA tekst (na dnu)</Label><Input value={editing.cta_text} onChange={(e) => setEditing({...editing, cta_text: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                                <div className="flex items-center gap-3">
                                    <Switch checked={editing.status === 'published'} onCheckedChange={(v) => setEditing({...editing, status: v ? 'published' : 'draft'})} />
                                    <Label>Objavljeno</Label>
                                </div>
                            </TabsContent>
                            <TabsContent value="faq" className="mt-4 space-y-3">
                                <Button size="sm" variant="outline" onClick={addFAQ} className="border-[#232A36] text-white hover:bg-[#232A36]"><Plus className="mr-1 h-3.5 w-3.5" />Dodaj pitanje</Button>
                                {(editing.faq || []).map((f, i) => (
                                    <div key={i} className="rounded-lg border border-[#232A36] p-3 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-white/60">Pitanje #{i+1}</span>
                                            <Button size="icon" variant="outline" onClick={() => removeFAQ(i)} className="border-red-900 text-red-400 h-7 w-7"><X className="h-3.5 w-3.5" /></Button>
                                        </div>
                                        <Input value={f.question} onChange={(e) => updateFAQ(i, 'question', e.target.value)} placeholder="Pitanje" className="bg-[#0B0C0E] border-[#232A36]" />
                                        <Textarea value={f.answer} onChange={(e) => updateFAQ(i, 'answer', e.target.value)} rows={2} placeholder="Odgovor" className="bg-[#0B0C0E] border-[#232A36]" />
                                    </div>
                                ))}
                            </TabsContent>
                            <TabsContent value="seo" className="space-y-4 mt-4">
                                <div><Label>SEO Title</Label><Input value={editing.seo?.meta_title || ''} onChange={(e) => setSeo('meta_title', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                                <div><Label>Meta Description</Label><Textarea value={editing.seo?.meta_description || ''} onChange={(e) => setSeo('meta_description', e.target.value)} rows={3} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                                <div><Label>Canonical URL</Label><Input value={editing.seo?.canonical_url || ''} onChange={(e) => setSeo('canonical_url', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                                <div><Label>OG Image URL</Label><Input value={editing.seo?.og_image || ''} onChange={(e) => setSeo('og_image', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2"><Switch checked={editing.seo?.robots_index !== false} onCheckedChange={(v) => setSeo('robots_index', v)} /><Label>Index</Label></div>
                                    <div className="flex items-center gap-2"><Switch checked={editing.seo?.include_in_sitemap !== false} onCheckedChange={(v) => setSeo('include_in_sitemap', v)} /><Label>U sitemap</Label></div>
                                </div>
                            </TabsContent>
                            <TabsContent value="linking" className="space-y-4 mt-4">
                                <div><Label>Povezane objave (slugovi, zarezom)</Label><Input value={(editing.related_posts||[]).join(', ')} onChange={(e) => setEditing({...editing, related_posts: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                                <div><Label>Povezane usluge (slugovi, zarezom)</Label><Input value={(editing.related_services||[]).join(', ')} onChange={(e) => setEditing({...editing, related_services: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                            </TabsContent>
                        </Tabs>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={close} className="border-[#232A36] text-white hover:bg-[#232A36]">Odustani</Button>
                        <Button onClick={onSave} disabled={saving} className="bg-[#B08D57] hover:bg-[#9c7d4e] text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Spremi'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
